import * as _ from 'lodash';
import { ModelType } from "~/enums/modelType";
import { ReferenceNodeType } from '~/enums/referenceNodeType';
import Guid from "~/helpers/guid";
import { ObjectHelper } from '~/helpers/objectHelper';
import { GenericType } from '~/interfaces/genericTypeInterfaces';
import {
  IImposter,
  IImposterFactory,
  IImposterMap,
  IImposterOptions
} from '~/interfaces/imposterInterfaces';
import {
  IProcessFlowVariable,
  ProcessFlowReferenceScopeType,
  ProcessFlowVariableType,
  IProcessFlow,
} from "~/interfaces/processFlowInterfaces";
import IRepo from "~/interfaces/repo";
import Validatable from "~/mixins/validatable";
import { ComparisonType } from "~/models/conditionalRules/comparisonType";
import IConditionalRuleReferencedControl from "~/models/conditionalRules/referencedControl";
import { TargetType } from "~/models/conditionalRules/targetType";
import { DatetimeMode } from "~/models/datetimeMode";
import ExternalConnection from '~/models/externalConnections/externalConnection';
import Imposter from '~/models/imposter';
import { commonImposterMap } from '~/models/imposterMapFactories/commonImposterMap';
import DefaultImposterMapFactory from '~/models/imposterMapFactories/defaultImposterMapFactory';
import ProcessFlowVariableValidation from "~/models/navigableValidations/processFlowVariableValidation";
import ObjectModel from "~/models/objectModel";
import ProcessFlowVariableDefinition from '~/models/processFlows/processFlowVariableDefinition';
import { ReferencePath } from '~/models/referencePaths/referencePath';
import { RelationType } from "~/models/relationType";
import ProcessFlowBuilderModel from "~/models/tracked/processFlowBuilderModel";
import { Logger } from "~/services/logger";
import Repo from "~/state/modules/repo";
import Form from "../../../formability/form_builder/models/form";
import FormElement from '../../../formability/form_builder/models/formElements/formElement';
import EndPoint from "../../../formability/organization_settings/models/connections/endPoint";
import DynamicInput from '../dynamicInput';
import Certificate from "../../../formability/certificateBuilder/models/certificate";
import CertificateDynamicInput from "../../../formability/certificateBuilder/models/certificateDynamicInput";

export default class ProcessFlowVariable extends Validatable(ProcessFlowBuilderModel) implements IProcessFlowVariable, IConditionalRuleReferencedControl, IImposterFactory {
  parent: IProcessFlow;
  name: string
  is_single: boolean
  variable_type: ProcessFlowVariableType
  reference_path: ReferencePath
  is_input: boolean
  is_input_required: boolean
  is_output: boolean
  is_default: boolean
  _nameInParent = "variables"
  sort_order: number

  static REFERENCABLE_VARIABLE_TYPES = [
    ModelType.ObjectModel,
    ModelType.Form,
    ModelType.FormSection,
    ModelType.FormElement,
    ModelType.EndPoint,
    ModelType.CertificateDynamicInput,
  ]

  static REFERENCABLE_SCOPE_VARIABLE_TYPES = [
    ModelType.FormElement,
    ModelType.FormSection,
    ModelType.EndPoint,
    ModelType.DynamicInput,
  ]

  constructor(parent, options?: { _track: boolean }) {
    super(options);
    this.parent = parent;
    this.defineProperty('client_id', Guid.create());
    this.defineProperty('name', '')
    this.defineProperty('is_single', true);
    this.defineProperty('variable_type', null, null, null, () => {
      let nodes = null
      switch(this.variable_type) {
        case ModelType.Form:
          nodes = [{reference_type: ReferenceNodeType.Form}]
          break;
        case ModelType.FormElement:
          nodes = [
            {reference_type: ReferenceNodeType.Form},
            {reference_type: ReferenceNodeType.FormElement},
          ]
          break;
        case ModelType.FormSection:
          nodes = [
            {reference_type: ReferenceNodeType.Form},
            {reference_type: ReferenceNodeType.FormSection},
          ]
          break;
        case ModelType.ObjectModel:
          nodes = [{reference_type: ReferenceNodeType.ObjectModel}]
          break;
        case ModelType.EndPoint:
          this.is_single = true
          nodes = [
            {reference_type: ReferenceNodeType.ApiConnection},
            {reference_type: ReferenceNodeType.ApiEndpoint},
            {reference_type: ReferenceNodeType.ApiMessageType},
          ]
          break;
        case ModelType.CertificateDynamicInput:
          nodes = [{reference_type: ReferenceNodeType.Certificate}];
          break;
      }

      if (nodes) {
        this.reference_path = this.initializeReferencePath({nodes})
      } else {
        if (this.reference_path) this.reference_path.destroy()
      }
    })
    this.defineProperty('is_input', false)
    this.defineProperty('is_input_required', false)
    this.defineProperty('is_output', false)
    this.defineProperty('sort_order', 0);
  }

  // ******************** RELATIONS AND REPOS******************** //

  static repo: IRepo<ProcessFlowVariable> = new Repo<ProcessFlowVariable>('processFlowVariables', ProcessFlowVariable, {});
  repo: IRepo<this>;

  addToRepo(): void {
    ProcessFlowVariable.repo.add(this)
  }

  get EMBEDDED_MODELS(): { [key: string]: { type: any, relationType: RelationType } } {
    return {
      'reference_path': {type: ReferencePath, relationType: RelationType.singleObject},
    }
  }

  createEmbeddedObject = {
    reference_path: (parent, json): ReferencePath => {
      return ReferencePath.repo.findOrCreateWith(_.compact([json.client_id, json.id]), parent, json, {
        nameInParent: 'reference_path',
        isCollectionRoot: false,
      }).loadFromJson(json)
    },
  }

  get rootName(): string {
    return this._nameInParent;
  }

  get isCollectionRoot(): boolean {
    return true;
  }

  initializeReferencePath(json = null) {
    if (!json) json = { client_id: Guid.create() }
    // make sure json has client_id or it will get saved in repo for the key +null+
    json.client_id = json.client_id || Guid.create()
    const path = this.createEmbeddedObject.reference_path(this, json)
    path.attachToParent(this)
    return path
  }

  // ******************** RELATIONS AND REPOS END******************** //

  get nameOrDefault(): string {
    if (this.name && this.name.length > 0) {
      return this.name;
    } else {
      return "Untitled Variable";
    }
  }

  // ******************** VALIDATABLE******************** //
  _validation: ProcessFlowVariableValidation<ProcessFlowVariable> = new ProcessFlowVariableValidation(this);

  getDisplayName(propName: string, _value?): string {
    switch (propName) {
      // TODO: Uncomment when front end validations are needed for ProcessFlow
      // case "variable_type":
      //   return "Variable Type"
      // case "reference_id":
      //   return "Reference"
      // case "reference_scope_id":
      //   return "Reference Scope"
      // case "is_single":
      //   return "Single Or Collection"
      // case "is_input":
      //   return "Is Input"
      // case "is_output":
      //   return "Is Output"
      // case "is_input_required":
      //   return "Is Input Required"
      // case "name":
      //   return "Name"
      default:
        return propName;
    }
  }
  // ******************** VALIDATABLE END******************** //

  get referenceRequired(): boolean {
    return ProcessFlowVariable.REFERENCABLE_VARIABLE_TYPES.includes(this.convertedWrappedVariableTypeFromNodes)
  }

  get referenceScopeRequired(): boolean {
    return ProcessFlowVariable.REFERENCABLE_SCOPE_VARIABLE_TYPES.includes(this.variable_type)
  }
  // ******************** SORTABLE******************** //

  get isLast(): boolean {
    return this.sort_order === this.parent.variablesHandler.lastSortOrder();
  }

  get isFirst(): boolean {
    return this.sort_order <= 0;
  }

  moveUp(): void {
    if (!this.isFirst) {
      this.sort_order--;
      this.parent.variablesHandler.updateElementPositions(this.parent, this, this.sort_order);
    }
  }

  moveDown(): void {
    if (!this.isLast) {
      this.sort_order++;
      this.parent.variablesHandler.updateElementPositions(this.parent, this, this.sort_order);
    }
  }
  // ******************** SORTABLE END******************** //

  get reference_id(): string {
    if (!this.reference_path) return null

    let referenceNodeIndex: number;
    switch (this.convertedWrappedVariableTypeFromNodes) {
      case ModelType.FormElement:
      case ModelType.FormSection: // intentional fall-through
      case ModelType.EndPoint:
        referenceNodeIndex = 1;
        break;
      case ModelType.CertificateDynamicInput:
        if (this.referenceScopeRequired) {
          referenceNodeIndex = 1;
        }
        break;
    }

    return this.reference_path.nodes[referenceNodeIndex || 0].reference_id;
  }

  // wrappedVariableType turns variable types into dynamic inputs
  // this is so that all variable types that could be a dynamic input
  // are read properly
  get wrappedVariableType(): ProcessFlowVariableType {
    switch(this.variable_type) {
      case ModelType.FormElement:
      case ModelType.FormSection:
        return ModelType.DynamicInput;
      default:
        return this.variable_type;
    }
  }

  set wrappedVariableType(variableType: ProcessFlowVariableType) {
    this.variable_type = variableType;
  }

  // converts a wrappedVariable from a dynamic input back to its original type based on its refernce path nodes
  get convertedWrappedVariableTypeFromNodes(): ProcessFlowVariableType {
    if (!this.reference_path) {
      return this.wrappedVariableType;
    }

    if (this.wrappedVariableType === ModelType.DynamicInput) {
      // check if reference nodes exist to return proper model type
      const node = _.first(this.reference_path.nodes);

      switch(node?.reference_type) {
        case ReferenceNodeType.Form:
          switch (_.last(this.reference_path.nodes).reference_type) {
            case ReferenceNodeType.FormSection:
              return ModelType.FormSection;
            case ReferenceNodeType.FormElement:
              return ModelType.FormElement;
            default:
              return this.wrappedVariableType;
          }
        case ReferenceNodeType.ApiConnection:
          return ModelType.EndPoint;
        case ReferenceNodeType.Certificate:
          return ModelType.CertificateDynamicInput;
        default:
          return this.wrappedVariableType;
      }
    }

    return this.wrappedVariableType;
  }

  get dynamicInputFromVariable(): DynamicInput | CertificateDynamicInput {
    if (!this.isVariableDynamicInput) {
      return null;
    }

    switch(this.convertedWrappedVariableTypeFromNodes) {
      case ModelType.EndPoint: {
        const connection =  ExternalConnection.repo.findBy(x => x.id === this.reference_scope_id);
        const endpoint: EndPoint = connection.endPointsArray().find((e) => e.id === this.reference_id)
        const inputs: DynamicInput[] = this.apiMessageType === 'request' ? endpoint.requestObjectArray : endpoint.responseObjectArray;
        return inputs.find(d => d.id === this.subObjectReferenceId);
      }
      case ModelType.CertificateDynamicInput:
        return this.referenceModel as CertificateDynamicInput;
      default: {
        return null;
      }
    }
  }

  get subObjectReferenceId(): string {
    if (!this.reference_path) return null
    const lastNode = _.last(this.reference_path.nodes);

    if (lastNode.reference_type === ReferenceNodeType.DynamicInput) {
      return lastNode.reference_id;
    }
    
    return null;
  }

  set subObjectReferenceId(val) {
    if (!this.reference_path) {
      return;
    }

    const lastNode = _.last(this.reference_path.nodes);

    if (lastNode && lastNode.reference_type === ReferenceNodeType.DynamicInput) {
      lastNode.reference_id = val;
    }
  }

  get reference_scope_type(): ProcessFlowReferenceScopeType {
    if (this.convertedWrappedVariableTypeFromNodes === ModelType.DynamicInput) return ModelType.DynamicInput;
    if (!this.reference_path) return null
    if (this.reference_path.nodes.length < 2) return null

    switch (this.reference_path.nodes[0].reference_type) {
      case ReferenceNodeType.Form:
        return ModelType.Form
      case ReferenceNodeType.ApiConnection:
        return ModelType.API
      case ReferenceNodeType.Certificate:
        return ModelType.Certificate;
      default:
        return null
    }
  }

  get reference_scope_id(): string {
    if (!this.reference_path) return null
    if (this.reference_path.nodes.length < 2) return null

    return this.reference_path.nodes[0].reference_id
  }

  get apiMessageType(): string {
    if (this.convertedWrappedVariableTypeFromNodes !== ModelType.EndPoint) return null
    if (!this.reference_path) return null

    return this.reference_path.nodes[2].reference_id
  }

  set apiMessageType(val) {
    this.reference_path.nodes[2].reference_id = val
  }

  isApi(): boolean {
    return this.convertedWrappedVariableTypeFromNodes === ModelType.EndPoint
  }

  get referenceModel() {
    switch (this.convertedWrappedVariableTypeFromNodes) {
      case ModelType.Form:
        return Form.repo.findBy(x => x.id === this.reference_id)
      case ModelType.FormElement:
        if (this.referenceScopeModel instanceof Form) {
          return this.referenceScopeModel.findElement(this.reference_id)
        }
        break;
      case ModelType.FormSection:
        if (this.referenceScopeModel instanceof Form) {
          return this.referenceScopeModel.formSections.find(formSection => formSection.id === this.reference_id);
        }
        break;
      case ModelType.ObjectModel:
        return ObjectModel.repo.findBy(x => x.id === this.reference_id)
      case ModelType.EndPoint:
        if (this.referenceScopeModel instanceof ExternalConnection) {
          return this.referenceScopeModel.endPointsArray().find((e) => e.id === this.reference_id)
        }
        break;
      case ModelType.CertificateDynamicInput:
        if (this.referenceScopeRequired && this.referenceScopeModel instanceof Certificate) { // for sub-object variables
          return this.referenceScopeModel.findCertificateDynamicInput(this.reference_id);
        }
        return Certificate.repo.findBy(x => x.id === this.reference_id); // for document dynamic input variables
      case ModelType.Text: // intentional fall-through
      case ModelType.File: // intentional fall-through
      case ModelType.Number:
      case ModelType.DynamicInput:
        return null
      default:
        Logger.errorOnce('ProcessFlowVariable#referenceModel getter', `reference for variable_type ${this.variable_type} not supported!`)
    }
  }

  /**
   *
   * @param val search results (i.e. json version) of the reference model
   */
  set referenceModel(val) {
    let referenceNodeIndex: number;
    switch (this.convertedWrappedVariableTypeFromNodes) {
      case ModelType.Form:
        Form.repo.addFromJson(val)
        break;
      case ModelType.FormElement:
        // loading not needed, should already exist on the chosen Form
        referenceNodeIndex = 1;
        if (val.modelType === ModelType.FormSection) {
          // updating variable_type re-creates reference_path nodes from scratch. Retain reference Form ID here.
          const formId = this.reference_path.nodes[0].reference_id;
          this.variable_type = ModelType.FormSection;
          this.reference_path.nodes[0].reference_id = formId;
        }
        break;
      case ModelType.ObjectModel:
        ObjectModel.repo.addFromJson(val)
        break;
      case ModelType.EndPoint:
        // loading not needed
        referenceNodeIndex = 1;
        break;
      case ModelType.CertificateDynamicInput:
        if (this.referenceScopeRequired) {
          // loading not needed, dynamic inputs already loaded through scope Certificate.
          referenceNodeIndex = 1;
          break;
        }
        Certificate.repo.addFromJson(val); // for "document dynamic input" variables
        break;
      default:
        Logger.errorOnce('ProcessFlowVariable#referenceModel setter', `reference for variable_type ${this.variable_type} not supported!`)
    }

    this.reference_path.nodes[referenceNodeIndex || 0].reference_id = val.id;
  }

  get imposterGenericModelType(): GenericType {
    return this.genericType;
  }

  get imposterModelType(): ModelType {
    return this.modelType;
  }

  get referenceScopeModel() {
    switch (this.reference_scope_type) {
      case ModelType.Form:
        return Form.repo.findBy(x => x.id === this.reference_scope_id)
      case ModelType.API:
        return ExternalConnection.repo.findBy(x => x.id === this.reference_scope_id)
      case ModelType.Certificate:
        return Certificate.repo.findBy(x => x.id === this.reference_scope_id);
    }
  }

  set referenceScopeModel(val) {
    // if the variable type is a sub object, set the variable type and create nodes
    if (this.wrappedVariableType === ModelType.DynamicInput) {
      let nodes = null
      switch(val.model_type) {
        case ModelType.Form:
          this.variable_type = ModelType.FormElement;
          nodes = [
            {reference_type: ReferenceNodeType.Form},
            {reference_type: ReferenceNodeType.FormElement}, // updated in set referenceModel() for FormSections
          ]
          break;
        case ModelType.API:
          this.variable_type = ModelType.DynamicInput;
          nodes = [
            {reference_type: ReferenceNodeType.ApiConnection},
            {reference_type: ReferenceNodeType.ApiEndpoint},
            {reference_type: ReferenceNodeType.ApiMessageType},
            {reference_type: ReferenceNodeType.DynamicInput},
          ]
          break;
        case ModelType.Certificate:
          this.variable_type = ModelType.DynamicInput;
          nodes = [
            {reference_type: ReferenceNodeType.Certificate},
            {reference_type: ReferenceNodeType.CertificateDynamicInput},
          ];
          break;
      }

      if (nodes) {
        this.reference_path = this.initializeReferencePath({nodes})
      } else {
        if (this.reference_path) this.reference_path.destroy()
      }
    }

    _.first(this.reference_path.nodes).reference_id = val.id
    switch (this.reference_scope_type) {
      case ModelType.Form:
        Form.repo.addFromJson(val)
        break;
      case ModelType.Certificate:
        Certificate.repo.addFromJson(val);
        break;
    }
  }

  afterDestroy() {
    super.afterDestroy()
    this.processFlow.variablesHandler.recalculateElementPositions()
  }

  // ******************** IConditionalRuleReferencedControl ******************** //
  isValidForConditionalLogic: boolean = true;
  get referenceNameOrDefault(): string {
    return this.name;
  }

  displayConditionType(targetType: TargetType): ComparisonType {
    // TODO: Implement based on single_choice and variable_type
    if (this.isInstanceCountComparisonType()) {
      return ComparisonType.instanceCount;
    } else if (this.isVariableText()) {
      return ComparisonType.text;
    } else if (this.isVariableNumber()) {
      return ComparisonType.numeric;
    } else if (this.isVariableBoolean()) {
      return ComparisonType.boolean;
    }
  }

  getChoiceComparisonOptions(): any[] {
    return [];
  }

  getConditionDateTimeMode(): DatetimeMode {
    return null;
  }

  getConditionMaxDateTime(): string {
    return "";
  }

  getConditionMinDateTime(): string {
    return "";
  }

  targetTypes(): TargetType[] {
    return [];
  }

  private isInstanceCountComparisonType(): boolean {
    return this.isCollection
  }
  // ******************** IConditionalRuleReferencedControl END******************** //

  isVariableForm(): this is { referenceModel: Form } {
    return this.variable_type === ModelType.Form
  }

  isVariableFormElement(): this is { referenceModel: FormElement, referenceScopeModel: Form } {
    return this.variable_type === ModelType.FormElement || this.variable_type === ModelType.FormSection;
  }

  isVariableObjectModel(): this is { referenceModel: ObjectModel } {
    return this.variable_type === ModelType.ObjectModel
  }

  isVariableApiEndPoint(): this is { referenceModel: EndPoint } {
    return this.variable_type === ModelType.EndPoint
  }

  isVariableDynamicInput(): this is { referenceModel: null } {
    return this.variable_type === ModelType.DynamicInput
  }

  isVariableDocumentDynamicInput(): this is { referenceModel: Certificate } {
    return this.variable_type === ModelType.CertificateDynamicInput;
  }

  isVariableFile(): this is { referenceModel: null } {
    return this.variable_type === ModelType.File
  }

  isVariableText(): this is { referenceModel: null } {
    return this.variable_type === ModelType.Text
  }

  isVariableNumber(): this is { referenceModel: null } {
    return this.variable_type === ModelType.Number
  }

  isVariableBoolean(): this is { referenceModel: null } {
    return this.variable_type === ModelType.Boolean
  }

  get modelType(): ModelType {
    return ModelType.ProcessFlowVariable
  }

  get genericType(): GenericType {
    return ProcessFlowVariableDefinition.genericType(this)
  }

  get referenceNodeType(): ReferenceNodeType {
    return ReferenceNodeType.ProcessFlowVariable
  }

  get isCollection(): boolean {
    return !this.is_single
  }

  imposterFor({ modelType, options }: { modelType: ModelType; options?: IImposterOptions }): IImposter {
    return new Imposter(this.imposterMapFor({ modelType, options }), options)
  }

  imposterMapFor({ modelType, options }: { modelType: ModelType; options?: IImposterOptions }): IImposterMap {
    let map: IImposterMap = commonImposterMap({
      destination: { modelType, genericType: options.genericType },
      source: { modelType: this.modelType, genericType: this.variable_type },
      options,
    })
    if (map) {
      ObjectHelper.merge(
        map,
        { isCollection: this.isCollection },
      )
    } else {
      map = new DefaultImposterMapFactory().imposterMapFrom(this)
    }

    return map
  }

  imposterForReference({ modelType, options }: { modelType: ModelType; options?: IImposterOptions }): IImposter {
    const factory: IImposterFactory = this.referenceRequired ? this.referenceModel : this;
    return factory.imposterFor({modelType, options})
  }
}